import React, { useRef, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';

const CTA = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!sectionRef.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    
    const elements = sectionRef.current.querySelectorAll('.reveal');
    elements.forEach(el => observer.observe(el));
    
    return () => {
      elements.forEach(el => observer.unobserve(el));
    };
  }, []);

  return (
    <section
      id="pricing"
      ref={sectionRef}
      className="py-24 relative overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 data-grid opacity-30"></div>
      <div className="absolute top-1/3 right-0 w-64 h-64 bg-blue-300/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/3 left-0 w-64 h-64 bg-purple-300/10 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-block px-3 py-1 mb-6 bg-blue-100 text-blue-600 rounded-full text-sm font-medium reveal opacity-0 transition-all duration-700 translate-y-8">
            Pricing Plans
          </div>
          <h2 className="heading-lg mb-6 reveal opacity-0 transition-all duration-700 translate-y-8" style={{ transitionDelay: '100ms' }}>
            Transparent Pricing for Every Fund Size
          </h2>
          <p className="text-gray-600 text-lg reveal opacity-0 transition-all duration-700 translate-y-8" style={{ transitionDelay: '200ms' }}>
            Contact us for a custom solution tailored to your specific requirements.
          </p>
          <div className="mt-8 reveal opacity-0 transition-all duration-700 translate-y-8" style={{ transitionDelay: '300ms' }}>
            <a
              href="#contact"
              className="btn-primary rounded-full inline-flex items-center group"
            >
              <span>Contact Us For Pricing</span>
              <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </div>

        <div className="mt-24 max-w-4xl mx-auto bg-blue-50 rounded-xl p-8 md:p-12 border border-blue-100 reveal opacity-0 transition-all duration-700 translate-y-8" style={{ transitionDelay: '400ms' }}>
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1">
              <h3 className="heading-md mb-4">Need a Custom Solution?</h3>
              <p className="text-gray-600 mb-6">
                Contact our team for a personalized quote tailored to your specific needs. We offer flexible solutions for funds of all sizes.
              </p>
              <a
                href="#contact"
                className="btn-primary rounded-full inline-flex items-center"
              >
                <span>Get a Custom Quote</span>
                <ArrowRight className="ml-2 w-4 h-4" />
              </a>
            </div>
            <div className="flex-1 md:border-l md:border-blue-200 md:pl-8">
              <div className="text-gray-600 mb-4 font-medium">Trusted by leading firms</div>
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-4 flex items-center justify-center h-16">
                  <div className="text-gray-400 font-medium">ACME Capital</div>
                </div>
                <div className="bg-white rounded-lg p-4 flex items-center justify-center h-16">
                  <div className="text-gray-400 font-medium">Venture Partners</div>
                </div>
                <div className="bg-white rounded-lg p-4 flex items-center justify-center h-16">
                  <div className="text-gray-400 font-medium">Growth Equity</div>
                </div>
                <div className="bg-white rounded-lg p-4 flex items-center justify-center h-16">
                  <div className="text-gray-400 font-medium">Alpha Funds</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTA;
